// eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
/* eslint-disable react-hooks/rules-of-hooks */
import * as React from 'react';
import {withSystem, TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
  updateExpressionAtAction,
} from '@packages/systems/ExpressionEditor-utils';
import {
  identity,
  constantTrue,
  constantFalse,
} from '@packages/utilities/fp/utils';
import {TNumber, TRecord, ENumber} from '@packages/systems/wfdl-ast';
import {styledDiv, emotionStyled} from '@packages/utilities/styled';

const {getIn} = ContextualExpressions;

const textareaStyles = {
  padding: '0px 4px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'rgb(33, 33, 33)',
  borderRadius: '2px',
  lineHeight: '22px',
  background: 'rgb(43, 43, 43)',
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  color: 'inherit',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  outline: '0px',
} as const;

// Using any as the return type here, this is just a kitchensink used type
const getNumberOrBinding = ContextualExpressions.matchExpression<any>({
  Number: identity,
  Variable: identity,
  Select: identity,
  _: () => null,
});
const isNumberVariableOrSelect = ContextualExpressions.matchExpression({
  Number: constantTrue,
  Variable: constantTrue,
  Select: constantTrue,
  _: constantFalse,
});

const getNumberOrBindingExpression = (expr: ContextualExpression) =>
  isNumberVariableOrSelect(stripAnn(expr)) ? expr : null;
const exprToColorValue = (expr: ContextualExpression, fieldName: string) => {
  const expression = getIn(expr, [{in: 'Record', at: fieldName}]);
  if (!expression) return null;
  return ContextualExpressions.getNumber(stripAnn(expression));
};
const exprToColorExpression = (
  expr: ContextualExpression,
  fieldName: string
) => {
  const expression = getIn(expr, [{in: 'Record', at: fieldName}]);
  if (!expression) return null;
  return getNumberOrBindingExpression(expression);
};
const useColor = (expr: ContextualExpression) => {
  const [r, setR] = React.useState(exprToColorValue(expr, 'r'));
  const [g, setG] = React.useState(exprToColorExpression(expr, 'g'));
  const [b, setB] = React.useState(exprToColorValue(expr, 'b'));

  React.useEffect(() => {
    const nextR = exprToColorValue(expr, 'r');
    if (nextR !== r) {
      setR(nextR);
    }
    const nextG = exprToColorExpression(expr, 'g');
    if (
      nextG !== null &&
      g !== null &&
      getNumberOrBinding(stripAnn(nextG)) !== getNumberOrBinding(stripAnn(g))
    ) {
      setG(nextG);
    }
    const nextB = exprToColorValue(expr, 'b');
    if (nextB !== b) {
      setB(nextB);
    }
  }, [expr]);

  const setColorValue = (value: any, property: string) => {
    if (property === 'r') {
      setR(value);
    }
    if (property === 'b') {
      setB(value);
    }
  };

  return {r, g, b, setColorValue};
};
const getColorRawValues = (expr: ContextualExpression) => {
  const r = exprToColorValue(expr, 'r');
  const g = exprToColorValue(expr, 'g');
  const b = exprToColorValue(expr, 'b');
  return {r, g, b};
};

const ColorBlock = styledDiv(({r, g, b}) => ({
  marginRight: '5px',
  height: '20px',
  width: '20px',
  backgroundColor: `rgba(${r || 0},${g || 0},${b || 0},1)`,
  color: 'black',
}));
const CenterAligned = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
});

export const Color = TypeImplementation.Alias({
  type: TRecord({
    r: TNumber,
    g: withSystem({isBindable: true}, TNumber),
    b: TNumber,
  }),
  renderSummary: ({expr}: {expr: ContextualExpression}) => {
    const {r, g, b} = getColorRawValues(expr);
    return (
      <CenterAligned>
        <ColorBlock r={r} g={g} b={b} />
        <span>
          r: {r || 0}, g: {g || 0}, b: {b || 0}
        </span>
      </CenterAligned>
    );
  },
  render: ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => any;
  }) => {
    const {r, g, b, setColorValue} = useColor(expr);

    if (r !== null && g !== null && b !== null) {
      const eeOnChange = React.useCallback(
        // @ts-expect-error - TS7006 - Parameter 'property' implicitly has an 'any' type.
        (property) => {
          return extendExpressionEditorActionAtPath(dispatch)([
            {in: 'Record', at: property},
          ]);
        },
        [dispatch]
      );

      const inputOnChange = React.useCallback(
        // @ts-expect-error - TS7006 - Parameter 'property' implicitly has an 'any' type.
        (property) => (e: any) => {
          setColorValue(e.target.value, property);
        },
        [setColorValue]
      );
      const inputOnBlur = React.useCallback(
        // @ts-expect-error - TS7006 - Parameter 'property' implicitly has an 'any' type.
        (property) => (e: any) => {
          const newExpression = ENumber(parseInt(e.target.value, 10));
          dispatch(
            updateExpressionAtAction(newExpression, [
              {in: 'Record', at: property},
            ])
          );
        },
        [dispatch]
      );
      return (
        <div>
          <input
            onChange={inputOnChange('r')}
            onBlur={inputOnBlur('r')}
            value={r}
            style={textareaStyles}
            data-automation-id="Color-r"
          />
          <ExpressionEditor dispatch={eeOnChange('g')} expression={g} />
          <input
            onChange={inputOnChange('b')}
            onBlur={inputOnBlur('b')}
            value={b}
            style={textareaStyles}
            data-automation-id="Color-b"
          />
        </div>
      );
    }
    return <ExpressionEditor dispatch={dispatch} expression={expr} />;
  },
});
